import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import {
  getCityHallRaidStatus,
  getCityHallRaidTargets,
  joinCityHallRaid,
  leaveCityHallRaid,
  voteCityHallRaid,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function CityHallRaidPage() {
  const queryClient = useQueryClient()
  const { collectionCards } = useGuardiansPassport()

  const statusQuery = useQuery({
    queryKey: ["city-hall-raid"],
    queryFn: getCityHallRaidStatus,
  })

  const targetsQuery = useQuery({
    queryKey: ["city-hall-raid-targets"],
    queryFn: getCityHallRaidTargets,
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["city-hall-raid"] })
  }

  const vote = useMutation({
    mutationFn: (cityId: number) => voteCityHallRaid(cityId),
    onSuccess: invalidate,
  })

  const join = useMutation({
    mutationFn: () => {
      const ids = collectionCards.slice(0, 3).map((c) => c.id)
      return joinCityHallRaid({ cardIds: ids })
    },
    onSuccess: invalidate,
  })

  const leave = useMutation({
    mutationFn: () => leaveCityHallRaid(),
    onSuccess: invalidate,
  })

  const raid = statusQuery.data

  return (
    <GameScreenLayout title="Рейд мэрии" backTo="/play/city-hall">
      {statusQuery.isLoading ? (
        <p className="text-sm text-guardians-muted">Загрузка…</p>
      ) : raid ? (
        <div className="space-y-4 text-sm">
          <p>
            {raid.isEventDay ? "Сегодня день рейда" : "Ожидание"} · защита{" "}
            {raid.hullPower} HP
          </p>
          {raid.myRaid ? (
            <div className="space-y-1">
              <p className="text-guardians-accent">Вы в рейде</p>
              {raid.myRaid.phase === "RESOLVED" ? (
                <Link
                  to={`/play/city-hall/raid/${encodeURIComponent(raid.myRaid.id)}`}
                  className="text-xs text-guardians-accent underline"
                >
                  Смотреть бой
                </Link>
              ) : null}
            </div>
          ) : null}
          {raid.incomingRaid?.phase === "RESOLVED" ? (
            <Link
              to={`/play/city-hall/raid/${encodeURIComponent(raid.incomingRaid.id)}`}
              className="text-xs text-guardians-accent underline"
            >
              Бой обороны
            </Link>
          ) : null}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={join.isPending}
              onClick={() => join.mutate()}
              className="rounded-xl bg-guardians-accent px-4 py-2 text-guardians-bg"
            >
              Вступить (3 карты)
            </button>
            <button
              type="button"
              disabled={leave.isPending}
              onClick={() => leave.mutate()}
              className="rounded-xl border border-white/20 px-4 py-2"
            >
              Выйти
            </button>
          </div>
        </div>
      ) : null}

      <section className="mt-6">
        <h3 className="mb-2 text-sm font-medium">Голосование за цель</h3>
        <ul className="space-y-2">
          {(targetsQuery.data ?? []).map((t) => (
            <li
              key={t.cityId}
              className="flex items-center justify-between rounded-lg border border-white/10 px-3 py-2 text-sm"
            >
              <span>{t.title}</span>
              <button
                type="button"
                disabled={vote.isPending}
                onClick={() => vote.mutate(t.cityId)}
                className="text-xs text-guardians-accent underline"
              >
                Голос
              </button>
            </li>
          ))}
        </ul>
      </section>

      {(vote.isError || join.isError || leave.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(
            vote.error ?? join.error ?? leave.error,
            "Ошибка рейда"
          )}
        </p>
      )}
    </GameScreenLayout>
  )
}
